import React, { useEffect, useRef } from "react";
import { pickColor, rgba, hexToRgb } from "./internal/colorUtils";

export const key = "lorenz-attractor";

const DEFAULT_BG = "#050912";
const DEFAULT_TRAIL_OLD = "#6366f1";
const DEFAULT_TRAIL_NEW = "#22d3ee";
const DEFAULT_HEAD = "#fde68a";

// The classic Lorenz system:
//   dx/dt = σ (y − x)
//   dy/dt = x (ρ − z) − y
//   dz/dt = x y − β z
// With (σ, ρ, β) = (10, 28, 8/3) the trajectory traces the famous
// "butterfly" strange attractor. We integrate with simple Euler at a
// small dt — Lorenz is stable enough at this scale that RK4 is overkill
// for a background visual.
const SIGMA = 10;
const RHO = 28;
const BETA = 8 / 3;
const DT = 0.005;
// Tracing speed = STEPS_PER_FRAME × DT attractor-time-units per frame.
// Kept deliberately low so the head crawls along the attractor and the
// viewer can actually follow the trajectory (rather than seeing a blur).
const STEPS_PER_FRAME = 6;
const TRAIL_MAX = 1800;

// Slow rotation of the 3D coordinate frame around the vertical (z) axis
// so both lobes of the butterfly come into view over time. Tuned to match
// the slower tracing speed — at the previous rate the camera spun faster
// than the head moved, which made the motion feel disconnected.
const ROTATION_SPEED = 0.00003; // rad/ms

function blendRgba(hexA, hexB, t, alpha) {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  if (!a || !b) return rgba(hexA, alpha);
  const clamp = Math.max(0, Math.min(1, t));
  const r = Math.round(a.r + (b.r - a.r) * clamp);
  const g = Math.round(a.g + (b.g - a.g) * clamp);
  const bl = Math.round(a.b + (b.b - a.b) * clamp);
  return `rgba(${r}, ${g}, ${bl}, ${alpha})`;
}

export default function LorenzAttractor({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([
    DEFAULT_BG,
    DEFAULT_TRAIL_OLD,
    DEFAULT_TRAIL_NEW,
    DEFAULT_HEAD,
  ]);
  // Mutable simulation state. Trail is a ring of {x, y, z} samples in
  // attractor units (not canvas px) so the slowly-rotating projection
  // can be re-applied every frame without losing fidelity.
  const stateRef = useRef({
    x: 0.1, y: 0, z: 0,
    trail: [],
  });

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_TRAIL_OLD),
      pickColor(colors, 2, DEFAULT_TRAIL_NEW),
      pickColor(colors, 3, DEFAULT_HEAD),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const tick = (ts) => {
      const [bg, oldCol, newCol, headCol] = colorsRef.current;
      const state = stateRef.current;

      // Integrate the system. Many small Euler steps per frame both
      // smooth the trail and let us cover a chunk of the attractor each
      // tick (otherwise it would crawl visibly).
      for (let i = 0; i < STEPS_PER_FRAME; i += 1) {
        const dx = SIGMA * (state.y - state.x);
        const dy = state.x * (RHO - state.z) - state.y;
        const dz = state.x * state.y - BETA * state.z;
        state.x += dx * DT;
        state.y += dy * DT;
        state.z += dz * DT;
        state.trail.push({ x: state.x, y: state.y, z: state.z });
      }
      // Cap trail length so memory and per-frame draw cost stay bounded.
      if (state.trail.length > TRAIL_MAX) {
        state.trail.splice(0, state.trail.length - TRAIL_MAX);
      }

      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);

      // Project to 2D. The attractor lives roughly in
      //   x ∈ [-20, 20], y ∈ [-25, 25], z ∈ [0, 50],
      // so we scale by min(W, H)/55 to fit comfortably with margin and
      // shift z down by 25 so it's centered vertically.
      const cx = width / 2;
      const cy = height / 2;
      const minDim = Math.min(width, height);
      const scale = minDim / 55;
      const angle = ts * ROTATION_SPEED;
      const cosA = Math.cos(angle);
      const sinA = Math.sin(angle);

      const project = (p) => {
        // Rotate around z-axis so the (x, y) plane spins; z stays
        // mapped to vertical screen position.
        const xr = p.x * cosA + p.y * sinA;
        const yr = -p.x * sinA + p.y * cosA;
        return {
          sx: cx + xr * scale,
          sy: cy + (25 - p.z) * scale,
          // y after rotation gives a fake "depth" cue used to dim
          // segments on the far side of the attractor.
          depth: yr,
        };
      };

      const trail = state.trail;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      // Draw the trail as a polyline broken into segments so each can
      // get its own age-based color and depth-based alpha. Using
      // stroke-per-segment is a few-percent more expensive than a
      // single beginPath, but it's what gives the attractor the
      // luminous, depth-aware look.
      const n = trail.length;
      // depthRange normalizes the rotated y into roughly [0..1] for alpha shading.
      // ±25 covers the attractor; ±30 gives a comfortable margin.
      const depthMax = 30;
      let prev = null;
      for (let i = 0; i < n; i += 1) {
        const proj = project(trail[i]);
        if (prev !== null) {
          const ageT = i / Math.max(1, n - 1); // 0 = oldest, 1 = newest
          const depthT = (proj.depth + depthMax) / (2 * depthMax); // 0 = far, 1 = near
          const depthClamp = Math.max(0, Math.min(1, depthT));
          // Newer + nearer segments are brighter; old/far ones recede.
          const alpha = (0.05 + ageT * 0.55) * (0.3 + depthClamp * 0.7);
          ctx.strokeStyle = blendRgba(oldCol, newCol, ageT, alpha);
          ctx.lineWidth = 0.6 + ageT * 1.3;
          ctx.beginPath();
          ctx.moveTo(prev.sx, prev.sy);
          ctx.lineTo(proj.sx, proj.sy);
          ctx.stroke();
        }
        prev = proj;
      }

      // Glowing head dot at the current state vector.
      if (prev) {
        const r = 3.4;
        const haloR = r * 5;
        const halo = ctx.createRadialGradient(prev.sx, prev.sy, 0, prev.sx, prev.sy, haloR);
        halo.addColorStop(0, rgba(headCol, 0.65));
        halo.addColorStop(1, rgba(headCol, 0));
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(prev.sx, prev.sy, haloR, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = rgba(headCol, 1);
        ctx.beginPath();
        ctx.arc(prev.sx, prev.sy, r, 0, Math.PI * 2);
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
